// Traduction des erreurs serveur du portail Pro.
//
// Les RPC et triggers Postgres (`pro_create_commande_atomic`,
// `pro_line_pricing_guard`, …) lèvent des codes bruts — `delivery_fee_mismatch`,
// `line_price_mismatch`, `pro_not_approved`. Tels quels ils n'apprennent rien à
// un électricien, et quand l'appelant n'affichait aucune erreur le bouton
// semblait simplement ne rien faire. Un seul endroit les traduit.

/** Signature minimale du `t` de react-i18next : clé + repli français. */
type Translate = (key: string, fallback: string) => string;

const SERVER_ERRORS: ReadonlyArray<{
  match: RegExp;
  key: string;
  fallback: string;
}> = [
  // ── Inscription (edge function pro-signup) ────────────────────────────
  {
    match: /^email_already_registered$/,
    key: 'pro.error.email_taken',
    fallback: 'Un compte existe déjà avec cet email. Connectez-vous.',
  },
  {
    match: /^invalid_email$/,
    key: 'pro.error.invalid_email',
    fallback: 'Adresse email invalide.',
  },
  {
    match: /^weak_password$/,
    key: 'pro.error.weak_password',
    fallback: 'Mot de passe trop court.',
  },
  {
    match: /^password_too_long$/,
    key: 'pro.error.password_too_long',
    fallback: 'Mot de passe trop long (72 caractères maximum).',
  },
  {
    match: /^too_many_signups$/,
    key: 'pro.error.too_many_signups',
    fallback: 'Trop de tentatives. Réessayez dans une heure.',
  },
  {
    match: /^unknown_tenant$/,
    key: 'pro.error.unknown_tenant',
    fallback: 'Aucun fournisseur configuré pour cette plateforme.',
  },
  {
    match: /^signup_failed$/,
    key: 'pro.error.signup_failed',
    fallback: "La création du compte a échoué. Réessayez dans un instant.",
  },
  // ── Documents (RPC / triggers) ────────────────────────────────────────
  {
    // Le prix d'une ligne ne correspond plus au catalogue (prix changé depuis).
    match: /line_price_mismatch|line_price_invalid_article/,
    key: 'pro.error.price_changed',
    fallback: 'Les prix ont changé. Actualisez la page puis réessayez.',
  },
  {
    // En-tête ≠ somme des lignes, ou frais/total recalculés côté serveur.
    match: /totals_mismatch|delivery_fee_mismatch|total_a_payer_mismatch/,
    key: 'pro.error.totals_stale',
    fallback:
      'Les montants ne sont plus à jour. Actualisez la page puis réessayez.',
  },
  {
    match: /pro_not_approved/,
    key: 'pro.error.not_approved',
    fallback: "Votre compte n'est pas encore validé par votre fournisseur.",
  },
  {
    match: /wallet_insufficient/,
    key: 'pro.error.wallet_insufficient',
    fallback: 'Solde portefeuille insuffisant.',
  },
  {
    match: /commission_exceeds_cap/,
    key: 'pro.error.commission_cap',
    fallback: 'Commission incohérente. Actualisez la page puis réessayez.',
  },
  {
    match: /invalid_line|invalid_amount/,
    key: 'pro.error.invalid_amount',
    fallback: 'Une ligne du document est invalide.',
  },
];

/** Message brut d'une erreur Supabase/PostgREST (qui n'est pas une `Error`). */
function rawMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  const e = err as {
    message?: string;
    details?: string;
    hint?: string;
  } | null;
  return e?.message ?? e?.details ?? e?.hint ?? String(err);
}

/** Message affichable pour l'électricien, traduit quand le code est connu. */
export function proErrorMessage(err: unknown, t: Translate): string {
  const raw = rawMessage(err);
  const known = SERVER_ERRORS.find((e) => e.match.test(raw));
  return known ? t(known.key, known.fallback) : raw;
}
